import { useState, useCallback } from 'react'
import { Input } from '@/components/ui/input'
import {
  formatBacktestValue,
  parseBacktestValue,
} from '@/features/backtest/utils/number-input-format'

interface BacktestNumberInputProps {
  value: number | string | null
  onChange: (value: number) => void
  kind?: 'int' | 'float'
  /**
   * Advisory only and currently NOT enforced. Typed values are never
   * hard-clamped to these — clamping silently rewrote inputs (e.g. ".0005"
   * jumped to a schema min of 0.1). Kept so call sites can pass schema
   * bounds; range validation belongs in the graph validator, not here.
   */
  min?: number
  max?: number
  step?: number
  /** Committed when the field is blurred while empty. Takes precedence over onEmpty. */
  defaultValue?: number
  /** Fires on empty blur when no defaultValue is set (e.g. clearing a filter bound). */
  onEmpty?: () => void
  maxSigFigs?: number
  disabled?: boolean
  className?: string
  placeholder?: string
  'aria-label'?: string
  'data-testid'?: string
}

/**
 * Numeric input shared across backtester node/param/filter fields.
 *
 * - Accepts typed K/M/B/T suffixes ("15k" → 15000) and small denominations.
 * - Shows the exact stored value while focused; compact form at rest.
 * - Commits on blur / Enter (matches the dashboard filter input).
 */
export function BacktestNumberInput({
  value,
  onChange,
  kind = 'float',
  // min/max are intentionally NOT destructured — they are advisory only and
  // must not clamp typed values (see interface).
  step,
  defaultValue,
  onEmpty,
  maxSigFigs,
  disabled,
  className,
  placeholder,
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: BacktestNumberInputProps) {
  const [focused, setFocused] = useState(false)
  const [rawText, setRawText] = useState('')

  const exactText = typeof value === 'number' ? String(value) : value != null ? String(value) : ''
  const restText =
    typeof value === 'number' && Number.isFinite(value)
      ? formatBacktestValue(value, maxSigFigs)
      : exactText

  const displayValue = focused ? rawText : restText

  const handleFocus = useCallback(() => {
    setFocused(true)
    setRawText(exactText)
  }, [exactText])

  const handleBlur = useCallback(() => {
    setFocused(false)
    const parsed = parseBacktestValue(rawText)
    if (parsed === null) {
      // Empty → default (if set) else onEmpty; unparseable → revert silently.
      if (rawText.trim() === '') {
        if (defaultValue !== undefined) onChange(defaultValue)
        else onEmpty?.()
      }
      return
    }
    const next = kind === 'int' ? Math.round(parsed) : parsed
    onChange(next)
  }, [rawText, kind, defaultValue, onEmpty, onChange])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setRawText(e.target.value)
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
  }, [])

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={className}
      placeholder={placeholder}
      step={step}
      aria-label={ariaLabel}
      data-testid={dataTestId}
    />
  )
}
